import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { isNil, omitBy } from 'lodash/fp'
import { PositionSettings, WorkdayComponentEnabledConfig } from '~/api/workdayTypes'
import { useSimpleGetQuery } from '~/api/common'
import fetch from '../common/fetch'

export const position_settings_url = '/api/v2/connectors/workday/position-settings'

const getPositionsForJob = (jobId: any, jobProfileId: any, timeTypeId: any, workerTypeId: any, locationId: any) => {
  const url = `/api/v2/connectors/workday/positions/?`

  const params = {
    job_id: jobId,
    restriction_job_profiles: jobProfileId,
    restriction_time_type_id: timeTypeId,
    restriction_contingent_worker_type_id: workerTypeId,
    restriction_locations: locationId
  }

  const positionParams = new URLSearchParams(omitBy(isNil)(params))

  return fetch.get(url + positionParams.toString())
}

const getPositionSettings = (): Promise<PositionSettings> => fetch.get(position_settings_url)

const getWorkdayContingentEnabled = (): Promise<WorkdayComponentEnabledConfig> =>
  fetch.get('/api/v2/connectors/workday/contingent-enabled')

const getWorkdayWorkerTrackingEnabled = (): Promise<WorkdayComponentEnabledConfig> =>
  fetch.get('/api/v2/connectors/workday/worker-tracking-enabled')

export const workdayApi = {
  getPositionsForJob,
  getWorkdayContingentEnabled,
  getWorkdayWorkerTrackingEnabled,
  getPositionSettings
}

const CONTINGENT_ENABLED_QUERY_KEY = 'workday_contingent_enabled'
const WORKER_TRACKING_ENABLED_QUERY_KEY = 'workday_worker_tracking_enabled'
const WORKDAY_POSITIONS_SETTINGS_KEY = 'workday_position_settings'

export const usePositionSettingsQuery = (options?: UseQueryOptions<PositionSettings>) =>
  useQuery({
    queryKey: [WORKDAY_POSITIONS_SETTINGS_KEY],
    queryFn: () => workdayApi.getPositionSettings(),
    ...options
  })

export const useUnfilledPositionCountByJobQuery = (job_id: number, options?: any) =>
  useSimpleGetQuery(`/api/v2/connectors/workday/positions/?job_id=${job_id}&is_open_for_onboarding=true`, {
    queryKey: ['workday_unfilled_positions_query', job_id],
    select: (data: any) => (data?.results && data?.results?.length > 0 ? data?.results?.length : 0),
    ...options
  })

export const useWorkdayContingentEnabled = () => {
  return useQuery<WorkdayComponentEnabledConfig>({
    queryKey: [CONTINGENT_ENABLED_QUERY_KEY],
    queryFn: () => workdayApi.getWorkdayContingentEnabled(),
    staleTime: Infinity
  })
}
export const useWorkdayWorkerTrackingEnabled = () => {
  const { data, isLoading } = useQuery({
    queryKey: [WORKER_TRACKING_ENABLED_QUERY_KEY],
    queryFn: () => workdayApi.getWorkdayWorkerTrackingEnabled()
  })
  return { isWorkdayWorkerTrackingEnabled: data?.enabled, isLoading }
}

/**
 * Hook to generate additional select filters for WD positions query
 *
 * This should still return something reasonable even if no generic_fields or location are provided
 *
 * @param generic_fields
 * @param location
 */
export function usePositionRestrictionQueries(
  generic_fields: Record<string, any> | undefined,
  location: number | null | undefined
) {
  const { data: positionSettings, isLoading } = usePositionSettingsQuery()
  const restriction_queries = []
  if (positionSettings) {
    const jobProfileUUID = positionSettings.job_profile_uuid
    const timeTypeUUID = positionSettings.time_type_uuid
    const workerTypeUUID = positionSettings.worker_type_uuid
    const jobProfileId = generic_fields?.[jobProfileUUID]
    const timeTypeId = generic_fields?.[timeTypeUUID]
    const workTypeId = generic_fields?.[workerTypeUUID]
    if (jobProfileId) {
      restriction_queries.push(`restriction_job_profiles=${jobProfileId}`)
    }
    if (timeTypeId) {
      restriction_queries.push(`restriction_time_type_id=${timeTypeId}`)
    }
    if (workTypeId) {
      restriction_queries.push(`restriction_contingent_worker_type_id=${workTypeId}`)
    }
  }
  if (location) {
    restriction_queries.push(`restriction_locations=${location}`)
  }
  return {
    isLoading,
    restrictions: restriction_queries
  }
}
